import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Edit2, Trash2, CheckCircle, Clock, Search, ChevronDown, ChevronUp, Tag, MessageCircle, FileText, Calendar, User } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function KnowledgeCenterManager() {
  const { admin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filter, setFilter] = useState({ category: '', service: '', pending: false });
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  const [formData, setFormData] = useState({
    category: 'service',
    service_name: '',
    country: '',
    question: '',
    answer: '',
    tags: ''
  });

  const getAuthHeader = () => ({
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
  });

  // Check if current user can edit/delete an entry
  const canModifyEntry = (entry) => {
    if (!admin || !admin.username) return false;
    
    // Admins can modify any entry
    const isAdmin = admin.role === 'super_admin' || admin.role === 'admin';
    if (isAdmin) return true;
    
    // Non-admins can only modify their own entries
    const isCreator = entry.created_by === admin.username;
    
    // Debug logging (remove after testing)
    if (!isAdmin && !isCreator) {
      console.log('Permission denied:', {
        entryCreator: entry.created_by,
        currentUser: admin.username,
        userRole: admin.role,
        isAdmin,
        isCreator
      });
    }
    
    return isCreator;
  };

  useEffect(() => {
    fetchEntries();
    fetchStats();
  }, [filter]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.category) params.append('category', filter.category);
      if (filter.service) params.append('service_name', filter.service);
      if (filter.pending) params.append('only_pending', 'true');

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/knowledge-center?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
      alert('Failed to fetch entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/knowledge-center/stats`,
        { headers: getAuthHeader() }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      };

      if (editingEntry) {
        await axios.put(
          `${BACKEND_URL}/api/admin/knowledge-center/${editingEntry.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        alert('Entry updated!');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/knowledge-center`,
          payload,
          { headers: getAuthHeader() }
        );
        alert('Entry created!');
      }

      setShowModal(false);
      setEditingEntry(null);
      resetForm();
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Failed to save entry');
    }
  };

  const handleApprove = async (entryId) => {
    const confirmed = await window.confirm('Approve this entry?');
    if (!confirmed) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/knowledge-center/${entryId}/approve`,
        {},
        { headers: getAuthHeader() }
      );
      alert('Entry approved!');
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error approving entry:', error);
      alert('Failed to approve entry');
    }
  };

  const handleDelete = async (entryId) => {
    const confirmed = await window.confirm('Delete this entry?');
    if (!confirmed) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/knowledge-center/${entryId}`,
        { headers: getAuthHeader() }
      );
      alert('Entry deleted!');
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Failed to delete entry');
    }
  };

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setFormData({
      category: entry.category,
      service_name: entry.service_name || '',
      country: entry.country || '',
      question: entry.question,
      answer: entry.answer,
      tags: entry.tags.join(', ')
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      category: 'service',
      service_name: '',
      country: '',
      question: '',
      answer: '',
      tags: ''
    });
  };

  const toggleEntry = (entryId) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId);
    } else {
      newExpanded.add(entryId);
    }
    setExpandedEntries(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter entries by search query
  const filteredEntries = entries.filter(entry => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      entry.question.toLowerCase().includes(query) ||
      entry.answer.toLowerCase().includes(query) ||
      (entry.service_name && entry.service_name.toLowerCase().includes(query)) ||
      (entry.tags && entry.tags.some(tag => tag.toLowerCase().includes(query)))
    );
  });

  return (
    <div className="knowledge-center-container" style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '32px',
        padding: '20px 0',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <div>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            color: '#1f2937',
            marginBottom: '8px'
          }}>
            <BookOpen size={32} style={{ color: '#8b5cf6' }} /> 
            Knowledge Center
          </h1>
          <p style={{ color: '#6b7280', fontSize: '15px' }}>Manage Q&A knowledge base for sales team</p>
        </div>
        <button 
          style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '15px',
            fontWeight: '600',
            boxShadow: '0 4px 6px rgba(139, 92, 246, 0.3)',
            transition: 'all 0.2s'
          }}
          onClick={() => { resetForm(); setShowModal(true); }}
          onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
        >
          <Plus size={18} /> Add Entry
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '8px' }}>{stats.total_entries}</h3>
            <p style={{ opacity: 0.9, fontSize: '14px' }}>Total Entries</p>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '8px' }}>{stats.approved_entries}</h3>
            <p style={{ opacity: 0.9, fontSize: '14px' }}>Approved</p>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '8px' }}>{stats.pending_entries}</h3>
            <p style={{ opacity: 0.9, fontSize: '14px' }}>Pending</p>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '8px' }}>{stats.verified_entries}</h3>
            <p style={{ opacity: 0.9, fontSize: '14px' }}>Verified</p>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr auto',
          gap: '12px',
          alignItems: 'center'
        }}>
          <div style={{ position: 'relative' }}>
            <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Search questions, answers, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>
          <select 
            value={filter.category} 
            onChange={(e) => setFilter({...filter, category: e.target.value})}
            style={{
              padding: '12px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">All Categories</option>
            <option value="general">General</option>
            <option value="service">Service</option>
            <option value="country">Country</option>
          </select>
          <input
            type="text"
            placeholder="Filter by service..."
            value={filter.service}
            onChange={(e) => setFilter({...filter, service: e.target.value})}
            style={{
              padding: '12px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '12px 16px',
            background: filter.pending ? '#fef3c7' : 'transparent',
            borderRadius: '8px',
            border: '2px solid #e5e7eb',
            whiteSpace: 'nowrap'
          }}>
            <input
              type="checkbox"
              checked={filter.pending}
              onChange={(e) => setFilter({...filter, pending: e.target.checked})}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Pending Only</span>
          </label>
        </div>
      </div>

      {/* Entries List */}
      <div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <p>Loading knowledge base...</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            background: 'white',
            borderRadius: '12px',
            border: '2px dashed #e5e7eb'
          }}>
            <BookOpen size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
            <p style={{ color: '#6b7280', fontSize: '16px' }}>
              {searchQuery ? 'No entries match your search' : 'No entries found. Create your first entry!'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filteredEntries.map(entry => {
              const isExpanded = expandedEntries.has(entry.id);
              return (
                <div 
                  key={entry.id} 
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    overflow: 'hidden',
                    transition: 'all 0.2s',
                    boxShadow: isExpanded ? '0 8px 16px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  {/* Entry Header */}
                  <div style={{ 
                    padding: '20px',
                    borderBottom: isExpanded ? '2px solid #f3f4f6' : 'none'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      marginBottom: '12px'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: entry.category === 'service' ? '#dbeafe' : entry.category === 'country' ? '#fef3c7' : '#f3e8ff',
                          color: entry.category === 'service' ? '#1e40af' : entry.category === 'country' ? '#92400e' : '#6b21a8'
                        }}>
                          {entry.category}
                        </span>
                        {entry.service_name && (
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: '#e0e7ff',
                            color: '#3730a3'
                          }}>
                            <FileText size={12} style={{ display: 'inline', marginRight: '4px' }} />
                            {entry.service_name}
                          </span>
                        )}
                        {entry.is_approved ? (
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: '#d1fae5',
                            color: '#065f46',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <CheckCircle size={12} /> Approved
                          </span>
                        ) : (
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: '#fef3c7',
                            color: '#92400e',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Clock size={12} /> Pending
                          </span>
                        )}
                        {entry.is_verified && (
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: '#dbeafe',
                            color: '#1e40af'
                          }}>
                            ✓ Verified
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {!entry.is_approved && (admin?.role === 'super_admin' || admin?.role === 'admin') && (
                          <button 
                            onClick={() => handleApprove(entry.id)}
                            style={{
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}
                          >
                            Approve
                          </button>
                        )}
                        {canModifyEntry(entry) && (
                          <>
                            <button 
                              onClick={() => openEditModal(entry)}
                              style={{
                                background: '#f3f4f6',
                                border: 'none',
                                padding: '8px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Edit entry"
                            >
                              <Edit2 size={16} color="#6b7280" />
                            </button>
                            <button 
                              onClick={() => handleDelete(entry.id)}
                              style={{
                                background: '#fef2f2',
                                border: 'none',
                                padding: '8px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Delete entry"
                            >
                              <Trash2 size={16} color="#ef4444" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Question */}
                    <div 
                      onClick={() => toggleEntry(entry.id)}
                      style={{ 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px'
                      }}
                    >
                      <MessageCircle size={20} style={{ color: '#8b5cf6', flexShrink: 0, marginTop: '2px' }} />
                      <h4 style={{ 
                        fontSize: '17px', 
                        fontWeight: '600',
                        color: '#1f2937',
                        flex: 1,
                        margin: 0,
                        lineHeight: '1.5'
                      }}>
                        {entry.question}
                      </h4>
                      <div style={{ flexShrink: 0 }}>
                        {isExpanded ? <ChevronUp size={20} color="#6b7280" /> : <ChevronDown size={20} color="#6b7280" />}
                      </div>
                    </div>

                    {/* Metadata: Created by and Created at */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '20px',
                      fontSize: '13px',
                      color: '#6b7280',
                      marginTop: '12px',
                      paddingTop: '12px',
                      borderTop: '1px solid #f3f4f6'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <User size={14} /> <strong>By:</strong> {entry.created_by}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={14} /> <strong>Created:</strong> {formatDate(entry.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Answer - Expandable */}
                  {isExpanded && (
                    <div style={{ padding: '20px', background: '#f9fafb' }}>
                      <div style={{
                        background: 'white',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        marginBottom: '16px'
                      }}>
                        <p style={{ 
                          fontSize: '15px',
                          lineHeight: '1.7',
                          color: '#374151',
                          margin: 0,
                          whiteSpace: 'pre-wrap'
                        }}>
                          {entry.answer}
                        </p>
                      </div>

                      {/* Tags */}
                      {entry.tags && entry.tags.length > 0 && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                          <Tag size={16} style={{ color: '#6b7280' }} />
                          {entry.tags.map((tag, idx) => (
                            <span 
                              key={idx}
                              style={{
                                padding: '4px 10px',
                                background: '#f3f4f6',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: '#4b5563'
                              }}
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Footer */}
                      <div style={{ 
                        display: 'flex', 
                        gap: '20px',
                        fontSize: '13px',
                        color: '#6b7280',
                        paddingTop: '12px',
                        borderTop: '1px solid #e5e7eb'
                      }}>
                        <span style={{ fontWeight: '500' }}>📊 Used {entry.times_used} times</span>
                        {entry.confidence_score && (
                          <span style={{ 
                            color: '#10b981',
                            fontWeight: '600'
                          }}>
                            ✓ {entry.confidence_score}% confidence
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Collapsed Preview */}
                  {!isExpanded && (
                    <div style={{ padding: '0 20px 20px 20px' }}>
                      <p style={{ 
                        fontSize: '14px',
                        color: '#6b7280',
                        margin: '12px 0 0 0',
                        lineHeight: '1.6'
                      }}>
                        {entry.answer.substring(0, 150)}...
                      </p>
                      <button
                        onClick={() => toggleEntry(entry.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#8b5cf6',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          padding: '8px 0',
                          marginTop: '8px'
                        }}
                      >
                        Read more →
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingEntry ? 'Edit Entry' : 'Add Entry'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Category *</label>
                <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} required>
                  <option value="general">General</option>
                  <option value="service">Service</option>
                  <option value="country">Country</option>
                </select>
              </div>
              <div className="form-group">
                <label>Service Name</label>
                <input value={formData.service_name} onChange={(e) => setFormData({...formData, service_name: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Question *</label>
                <textarea value={formData.question} onChange={(e) => setFormData({...formData, question: e.target.value})} rows="2" required />
              </div>
              <div className="form-group">
                <label>Answer *</label>
                <textarea value={formData.answer} onChange={(e) => setFormData({...formData, answer: e.target.value})} rows="5" required />
              </div>
              <div className="form-group">
                <label>Tags (comma-separated)</label>
                <input value={formData.tags} onChange={(e) => setFormData({...formData, tags: e.target.value})} placeholder="visa, family, PR" />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
